import React, { useEffect, useState } from 'react';
import './femaleVoice';

// Shared sound settings for the built-in Learning Zone games (Jigsaw, Color &
// Mix, Block Coding, Odd One Out, Fix the Pattern): one volume level that
// scales every beep and every spoken line, persisted like the other games'
// `tapGameVolume`, plus a "replay" slot each game fills with its current
// instruction so the Replay button can say it again.

const KEY = 'lzGameVolume';

const read = () => {
    try {
        const v = parseFloat(window.localStorage.getItem(KEY));
        return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
    } catch (e) {
        return 1;
    }
};

let volume = typeof window === 'undefined' ? 1 : read();
let lastAudible = volume > 0 ? volume : 1;
let replay = null;
const listeners = new Set();

export const getVolume = () => volume;

export const setVolume = (v) => {
    volume = Math.min(1, Math.max(0, Number(v) || 0));
    if (volume > 0) lastAudible = volume;
    try { window.localStorage.setItem(KEY, String(volume)); } catch (e) { /* ignore */ }
    if (volume === 0) {
        try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
    }
    listeners.forEach((fn) => fn(volume));
};

export const toggleMute = () => setVolume(volume > 0 ? 0 : lastAudible);

export const useVolume = () => {
    const [v, setV] = useState(volume);
    useEffect(() => {
        listeners.add(setV);
        setV(volume);
        return () => listeners.delete(setV);
    }, []);
    return v;
};

// Emoji read out loud sound odd ("red circle"), so they're stripped first.
const EMOJI = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2B50}\u{2B06}\u{2194}-\u{21FF}\u{FE0F}\u{200D}]/gu;

export const speakText = (text) => {
    if (!volume || !text) return;
    try {
        const synth = window.speechSynthesis;
        if (!synth || typeof window.SpeechSynthesisUtterance === 'undefined') return;
        synth.cancel();
        const u = new window.SpeechSynthesisUtterance(String(text).replace(EMOJI, ''));
        u.rate = 0.9;
        u.pitch = 1.3;
        u.volume = volume;
        synth.speak(u);
    } catch (e) { /* never block a game on speech */ }
};

// Short Web Audio beeps scaled by the shared volume (same tones the older
// Learning Zone games build inline).
let audioCtx = null;
export const playTone = (freq, delayMs = 0, gain = 0.15, dur = 0.18, type = 'sine') => {
    if (!volume) return;
    try {
        if (!audioCtx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            audioCtx = new Ctx();
        }
        if (audioCtx.state === 'suspended') audioCtx.resume();
        const t = audioCtx.currentTime + delayMs / 1000;
        const osc = audioCtx.createOscillator();
        const g = audioCtx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        g.gain.setValueAtTime(gain * volume, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(g).connect(audioCtx.destination);
        osc.start(t);
        osc.stop(t + dur + 0.02);
    } catch (e) { /* ignore */ }
};
export const sfx = {
    tap: () => playTone(600, 0, 0.1, 0.12),
    correct: () => [523, 659, 784].forEach((f, i) => playTone(f, i * 120)),
    wrong: () => playTone(180, 0, 0.1, 0.25, 'triangle'),
    finish: () => [523, 659, 784, 1047].forEach((f, i) => playTone(f, i * 130, 0.16, 0.25)),
};

// Each game registers what "Replay" should say for its current screen.
export const setReplay = (textOrFn) => { replay = textOrFn; };
export const runReplay = () => {
    const text = typeof replay === 'function' ? replay() : replay;
    speakText(text);
};

// Each game also reports its score and current question here so the
// shared GameHeaderBar above the board can show them:
// { score, total, scoreLabel, scoreIcon, questionLabel, question }.
let hud = null;
const hudListeners = new Set();

export const setHud = (next) => {
    hud = next;
    hudListeners.forEach((fn) => fn(hud));
};

export const useHud = () => {
    const [h, setH] = useState(hud);
    useEffect(() => {
        hudListeners.add(setH);
        setH(hud);
        return () => hudListeners.delete(setH);
    }, []);
    return h;
};

export const VolumeBar = ({ className = '' }) => {
    const v = useVolume();
    const icon = v === 0 ? '🔇' : v < 0.5 ? '🔉' : '🔊';
    return (
        <div className={`lz-volume ${className}`}>
            <button type="button" className="lz-volume-icon" onClick={toggleMute} title={v === 0 ? 'Unmute' : 'Mute'} aria-label={v === 0 ? 'Unmute' : 'Mute'}>
                {icon}
            </button>
            <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={v}
                aria-label="Volume"
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                style={{ '--lz-vol': `${Math.round(v * 100)}%` }}
            />
            <span className="lz-volume-value">{Math.round(v * 100)}%</span>
            <button type="button" className="lz-volume-replay" onClick={runReplay} title="Replay" aria-label="Replay">
                <span className="material-symbols-outlined">replay</span>
            </button>
            <style>{`
                .lz-volume {
                    display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border-radius: 14px;
                    background: rgba(255, 255, 255, 0.86); box-shadow: 0 1px 4px rgba(224,198,138,0.10);
                    font-weight: 700; color: #b8860b;
                }
                .lz-volume-icon { border: none; background: none; padding: 0; font-size: 20px; cursor: pointer; line-height: 1; }
                .lz-volume input[type=range] {
                    width: 110px; height: 6px; border-radius: 3px; cursor: pointer; appearance: none; -webkit-appearance: none;
                    background: linear-gradient(90deg, #1976d2 var(--lz-vol), #d6dbe3 var(--lz-vol));
                }
                .lz-volume input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #1976d2; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.3); }
                .lz-volume input[type=range]::-moz-range-thumb { width: 14px; height: 14px; border-radius: 50%; background: #1976d2; border: 2px solid #fff; }
                .lz-volume-value { min-width: 40px; text-align: right; font-size: 1rem; color: #1976d2; font-weight: 700; }
                .lz-volume-replay {
                    display: inline-flex; align-items: center; justify-content: center; border-radius: 12px; padding: 2px 8px;
                    border: 1px solid #6c757d; background: transparent; color: #6c757d; cursor: pointer;
                }
                .lz-volume-replay:hover { background: #6c757d; color: #fff; }
                .lz-volume-replay .material-symbols-outlined { font-size: 20px; }
                @media (max-width: 575px) { .lz-volume input[type=range] { width: 70px; } }
            `}</style>
        </div>
    );
};
